import { EyeIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";

import CompaniesHouseIcon from "src/images/companiesHouse-icon.png";

import {
  InfoBoxWidget,
  InfoBoxWidgetTitleArea,
  KeyFact,
  KeyFactLinkSeeMore,
} from "../Styles";

type CompanyCompaniesHouseProps = {
  company: OpenAPI.Schemas["InnointelCompanies"];
  isPDF?: boolean;
};

const CompanyCompaniesHouse = ({
  company,
  isPDF,
}: CompanyCompaniesHouseProps) => {
  const { companyNumber, legalName, name } = company;

  const companiesHouse = company?.companiesHouse;

  if (
    companiesHouse?.accounts ||
    companiesHouse?.confirmationStatement ||
    companiesHouse?.officers
  ) {
    const {
      accounts = {},
      confirmationStatement: statement,
      officers: allOfficers = [],
    } = companiesHouse;
    const { last_accounts: lastAccounts } = accounts;
    const officers = allOfficers.filter((o) => !o.resigned_on);

    return (
      <InfoBoxWidget>
        <InfoBoxWidgetTitleArea $alignItems="center">
          <Image src={CompaniesHouseIcon} size="small" />
          <div>
            <H3>Companies House</H3>
            <span>Number: {companyNumber}</span>
          </div>
        </InfoBoxWidgetTitleArea>
        <KeyFact title="Company name" fact={legalName || name} />
        {officers.length > 0 ? (
          <KeyFact
            title="Company directors"
            fact={`${officers
              .slice(0, 6)
              .map(
                (officer) =>
                  `${officer.name}${officer.country_of_residence ? ` (${officer.country_of_residence})` : ""}`,
              )
              .join(
                ", ",
              )} ${officers.length > 6 ? ` + ${officers.length - 6} more` : ""}`}
          />
        ) : null}
        {companiesHouse.sicCodes ? (
          <KeyFact
            title="SIC Codes"
            fact={`${companiesHouse.sicCodes.join(", ")}`}
          />
        ) : null}
        {lastAccounts ? (
          <KeyFact
            title="Last accounts"
            fact={`${lastAccounts.type}, made up to ${lastAccounts.made_up_to}`}
          />
        ) : null}
        {statement ? (
          <KeyFact
            title="Confirmation status"
            fact={`Last made up to ${statement.last_made_up_to}. Next due ${statement.next_due}`}
          />
        ) : null}
        {!isPDF ? (
          <KeyFactLinkSeeMore>
            <ButtonLink
              size="small"
              color="secondary"
              target="_blank"
              rel="noopener noreferrer"
              to={`https://find-and-update.company-information.service.gov.uk/company/${companyNumber}`}
            >
              <Icon icon={EyeIcon} /> View on Companies House
            </ButtonLink>
          </KeyFactLinkSeeMore>
        ) : null}
      </InfoBoxWidget>
    );
  }
  return null;
};

export default CompanyCompaniesHouse;
